import { type KeyboardEvent, useCallback, useMemo } from 'react'
import { type Observable } from 'rxjs'
import {
  areSelectOptionsEqual,
  type SelectOption,
} from '@/components/inputs/select-option'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { isNotNil } from '@/utils/object-util'

export interface AdvancedSelectProps {
  multi: boolean
  options: SelectOption[]
  selected: SelectOption[]
  isDisabled: boolean
  onChange: (selections: SelectOption[]) => unknown
  focus$?: Observable<unknown>
  onFocus?: () => unknown
  onEnterDown?: (e: KeyboardEvent) => unknown
  isNotValid?: boolean
  testId?: string
}

export function AdvancedSelect({
  multi,
  options,
  selected,
  isDisabled,
  onChange,
  onEnterDown,
  focus$,
  onFocus,
  isNotValid,
  testId,
}: AdvancedSelectProps) {
  const itemSource = useMemo(
    () =>
      ItemSource.static<SelectOption>(options, (item, text) =>
        item.text.toLowerCase().includes(text.toLowerCase()),
      ),
    [options],
  )
  const change = useCallback(
    (values: SelectOption | SelectOption[] | undefined) =>
      onChange(Array.isArray(values) ? values : [values].filter(isNotNil)),
    [onChange],
  )
  const itemToString = useCallback((item: SelectOption) => item.text, [])
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...selectProps}
      focus$={focus$}
      onFocus={onFocus}
      isDisabled={isDisabled}
      persist={multi}
      onEnterDown={onEnterDown}
      itemToString={itemToString}
      areItemsEqual={areSelectOptionsEqual}
      multi={multi}
      value={multi ? selected : selected[0]}
      onChange={change}
      placeholder={multi ? 'Select all that apply' : 'Select one'}
      isNotValid={isNotValid}
      testId={testId}
    />
  )
}
